import React, { useRef, useState } from 'react';
import { Customer } from '../../types';
import { fetchCustomers, createCustomer, restoreCustomer } from '../../services/crmService';
import { Button } from '../Button';
import { AlertCircle, Building2, Loader2, Plus, User, X } from 'lucide-react';
import { DuplicateCustomerDialog } from './DuplicateCustomerDialog';
import { DuplicateHit, findSimilarCustomers } from '../../services/customerDuplicates';
import { composePersonName } from '../../services/personName';
import { rechtFehlerOder } from '../../services/rechte/rechtFehler';

/**
 * "Neuer Kunde" — Anlage mit Dublettenpruefung. Gemeinsam fuer die
 * Kundenliste und den "Neues Angebot"-Dialog: dort fehlte der Weg, und wer
 * einem Neukunden ein Angebot schreiben wollte, musste abbrechen, den Kunden
 * im CRM anlegen und das Angebot von vorn beginnen.
 *
 * Was nach der Anlage geschieht (Akte oeffnen, Kunde ins Angebot uebernehmen),
 * entscheidet der Aufrufer; er schliesst den Dialog auch.
 */
interface Props {
    /** Vorbelegung des Firmennamens, z. B. der Suchbegriff aus dem Kunden-Picker. */
    initialName?: string;
    /** Neu angelegt. */
    onCreated: (customer: Customer) => void;
    /**
     * Statt neu anzulegen einen vorhandenen Kunden genommen (Dublettendialog).
     * `restored`: er war archiviert und ist jetzt wieder aktiv.
     */
    onUseExisting: (customer: Customer, restored: boolean) => void;
    /** Beschriftung fuer "vorhandenen nehmen" im Dublettendialog. */
    useExistingLabel?: string;
    /**
     * Archivierte Treffer ohne Wiederherstellen nehmen? Die Kundenakte darf
     * sie oeffnen; ein neues Angebot soll nicht an einem archivierten Kunden
     * haengen.
     */
    useArchivedExisting?: boolean;
    onClose: () => void;
}

export const NewCustomerDialog: React.FC<Props> = ({
    initialName = '',
    onCreated,
    onUseExisting,
    useExistingLabel,
    useArchivedExisting = true,
    onClose,
}) => {
    const [newName, setNewName] = useState(initialName);
    // Privatperson: Vor- und Nachname getrennt; der Vollname (newName-Aequivalent)
    // wird daraus komponiert. Anrede/Titel bleiben dem Detail vorbehalten —
    // der Dialog soll kurz bleiben.
    const [newFirstName, setNewFirstName] = useState('');
    const [newLastName, setNewLastName] = useState('');
    const [newType, setNewType] = useState<NonNullable<Customer['customerType']>>('company');
    const [creating, setCreating] = useState(false);
    const [error, setError] = useState<string | null>(null);
    // Dubletten-Treffer der Neuanlage — oeffnet den Entscheidungsdialog.
    const [duplicateHits, setDuplicateHits] = useState<DuplicateHit[] | null>(null);

    /** Name, unter dem der neue Kunde ueberall erscheint (PDF, Suche, Belege). */
    const composedNewName = newType === 'person'
        ? composePersonName(newFirstName, newLastName)
        : newName.trim();

    // Eine Pruefung bzw. Anlage zur Zeit. `creating` allein reicht nicht: ein
    // Doppelklick (auch auf „Trotzdem neu anlegen“ im Dublettendialog) kommt
    // an, bevor der Zustand neu gezeichnet ist — und legte den Kunden bei
    // langsamer Antwort zweimal an, mit zwei Kundennummern.
    const laeuftRef = useRef(false);

    /** Die eigentliche Anlage — laeuft erst NACH bestandener Dublettenpruefung. */
    const doCreate = async (name: string) => {
        if (laeuftRef.current) return;
        laeuftRef.current = true;
        // Dublettendialog zu: sonst liefe die Anlage unsichtbar darunter, und
        // ein Klick auf „Übernehmen“ waehlte den vorhandenen Kunden, waehrend
        // der neue trotzdem entsteht.
        setDuplicateHits(null);
        setCreating(true);
        setError(null);
        let created: Customer;
        try {
            created = await createCustomer({
                name,
                customerType: newType,
                firstName: newType === 'person' ? newFirstName.trim() || undefined : undefined,
                lastName: newType === 'person' ? newLastName.trim() || undefined : undefined,
                contacts: [],
                // KEINE Vorbelegung: ein Zahlungsziel am Kunden ist die Ausnahme.
                // Ohne Eintrag gilt die Grundeinstellung des Rechnungsmoduls.
                defaultDiscountPercent: 0,
            });
        } catch (e) {
            setDuplicateHits(null);
            setError('Kunde konnte nicht angelegt werden: ' + rechtFehlerOder(e));
            laeuftRef.current = false;
            setCreating(false);
            return;
        }
        // Sperre bleibt nach Erfolg zu: der Aufrufer schliesst den Dialog, ein
        // Klick dazwischen soll nicht noch einen Kunden anlegen.
        setCreating(false);
        onCreated(created);
    };

    const handleCreate = async () => {
        const name = composedNewName;
        if (!name || laeuftRef.current) return;
        laeuftRef.current = true;
        setCreating(true);
        setError(null);
        let hits: DuplicateHit[];
        try {
            // Auch gegen ARCHIVIERTE pruefen: sonst legt man den Kunden neu an, den
            // man gestern archiviert hat, und haette zwei Nummern fuer eine Firma.
            const all = await fetchCustomers({ includeArchived: true });
            hits = findSimilarCustomers({ name }, all);
        } catch (e) {
            setError('Dublettenprüfung fehlgeschlagen: ' + rechtFehlerOder(e));
            laeuftRef.current = false;
            setCreating(false);
            return;
        }
        laeuftRef.current = false;
        setCreating(false);
        if (hits.length) {
            setDuplicateHits(hits);
            return;
        }
        await doCreate(name);
    };

    const handleRestore = async (c: Customer) => {
        if (laeuftRef.current) return;
        try {
            await restoreCustomer(c.id);
        } catch (e) {
            setDuplicateHits(null);
            setError('Wiederherstellen fehlgeschlagen: ' + rechtFehlerOder(e));
            return;
        }
        setDuplicateHits(null);
        onUseExisting({ ...c, archivedAt: undefined, archivedBy: undefined }, true);
    };

    const onEnter = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && composedNewName && !creating) void handleCreate();
    };

    return (
        <>
            {/* z-[110]: liegt auch ueber dem "Neues Angebot"-Dialog (z-[100]);
                der Dublettendialog (z-[120]) wiederum ueber diesem. */}
            <div className="fixed inset-0 z-[110] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in">
                <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden animate-scale-in">
                    <div className="bg-slate-900 text-white p-6 flex justify-between items-center">
                        <div className="flex items-center gap-3">
                            <div className="bg-blue-500/20 p-2 rounded-xl border border-blue-500/30"><Building2 className="w-6 h-6 text-blue-400" /></div>
                            <h3 className="font-bold text-lg">Neuer Kunde</h3>
                        </div>
                        <button onClick={onClose} disabled={creating} className="hover:bg-white/10 p-2 rounded-full transition-all disabled:opacity-50"><X className="w-5 h-5" /></button>
                    </div>
                    <div className="p-8 space-y-2">
                        <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-widest ml-1">Kundentyp</label>
                        <div className="inline-flex rounded-xl border border-slate-200 overflow-hidden mb-4">
                            {([
                                { key: 'company' as const, label: 'Unternehmen', icon: <Building2 className="w-4 h-4" /> },
                                { key: 'person' as const, label: 'Privatperson', icon: <User className="w-4 h-4" /> },
                            ]).map((t) => (
                                <button
                                    key={t.key}
                                    type="button"
                                    onClick={() => setNewType(t.key)}
                                    aria-pressed={newType === t.key}
                                    className={`flex items-center gap-2 px-5 py-2.5 text-sm font-bold whitespace-nowrap border-b-2 transition-all ${
                                        newType === t.key
                                            ? 'border-blue-600 text-blue-600 bg-blue-50/50'
                                            : 'border-transparent text-slate-400 hover:text-slate-600 hover:bg-slate-50'
                                    }`}
                                >
                                    {t.icon}
                                    {t.label}
                                </button>
                            ))}
                        </div>
                        {newType === 'person' ? (
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <div>
                                    <label className="text-[11px] font-bold text-slate-500 uppercase tracking-widest ml-1">Vorname</label>
                                    <div className="relative mt-1">
                                        <User className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-300" />
                                        <input
                                            autoFocus
                                            value={newFirstName}
                                            onChange={(e) => setNewFirstName(e.target.value)}
                                            onKeyDown={onEnter}
                                            className="w-full pl-10 pr-4 py-3 border border-slate-200 rounded-xl focus:ring-4 focus:ring-blue-50 focus:border-blue-500 outline-none transition-all font-bold"
                                            placeholder="Vorname"
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label className="text-[11px] font-bold text-slate-500 uppercase tracking-widest ml-1">Nachname *</label>
                                    <input
                                        value={newLastName}
                                        onChange={(e) => setNewLastName(e.target.value)}
                                        onKeyDown={onEnter}
                                        className="w-full mt-1 px-4 py-3 border border-slate-200 rounded-xl focus:ring-4 focus:ring-blue-50 focus:border-blue-500 outline-none transition-all font-bold"
                                        placeholder="Nachname"
                                    />
                                </div>
                            </div>
                        ) : (
                            <>
                                {/* block: sonst stand „NAME /“ als Inline-Text neben dem Kundentyp-Umschalter. */}
                                <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-widest ml-1">
                                    Name / Firma *
                                </label>
                                <div className="relative">
                                    <Building2 className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-300" />
                                    <input
                                        autoFocus
                                        value={newName}
                                        onChange={(e) => setNewName(e.target.value)}
                                        onKeyDown={onEnter}
                                        className="w-full pl-10 pr-4 py-3 border border-slate-200 rounded-xl focus:ring-4 focus:ring-blue-50 focus:border-blue-500 outline-none transition-all font-bold"
                                        placeholder="Firmenname eingeben…"
                                    />
                                </div>
                            </>
                        )}
                        {error && (
                            <div className="flex items-start gap-2 text-xs text-red-700 bg-red-50 border border-red-100 rounded-xl px-3 py-2 mt-3">
                                <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                                <span>{error}</span>
                            </div>
                        )}
                    </div>
                    <div className="p-6 bg-slate-50 border-t border-slate-100 flex justify-end gap-3">
                        <Button variant="ghost" onClick={onClose} disabled={creating}>Abbrechen</Button>
                        <Button variant="primary" onClick={handleCreate} disabled={!composedNewName || creating} className="shadow-lg border-b-2 border-blue-700" icon={creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}>
                            Anlegen
                        </Button>
                    </div>
                </div>
            </div>

            {/* Dubletten-Dialog der Neuanlage */}
            {duplicateHits && (
                <DuplicateCustomerDialog
                    hits={duplicateHits}
                    candidateName={composedNewName}
                    openLabel={useExistingLabel}
                    openArchived={useArchivedExisting}
                    onOpenExisting={(c) => {
                        if (laeuftRef.current) return;
                        setDuplicateHits(null);
                        onUseExisting(c, false);
                    }}
                    onRestore={handleRestore}
                    onCreateAnyway={() => void doCreate(composedNewName)}
                    onClose={() => setDuplicateHits(null)}
                />
            )}
        </>
    );
};
